import React from 'react';
import { useAuth } from '../context/AuthContext';
import { useWallet } from '@solana/wallet-adapter-react';

type ViewType = 'home' | 'profile' | 'notifications' | 'messages' | 'collections' | 'subscriptions' | 'analytics' | 'creator-application' | 'admin-dashboard' | 'kyc-admin';

interface DrawerMenuProps {
    onCreatePost: () => void;
    onViewChange?: (view: ViewType) => void;
}

const DrawerMenu: React.FC<DrawerMenuProps> = ({ onCreatePost, onViewChange }) => {
    const { disconnect, connected } = useWallet();
    const { logout, user } = useAuth();
    const handleCreatePost = () => {
        onCreatePost();
        // Close offcanvas programmatically
        const offcanvas = document.getElementById('drawerOffcanvas');
        if (offcanvas) {
            const bsOffcanvas = (window as any).bootstrap?.Offcanvas?.getInstance(offcanvas);
            bsOffcanvas?.hide();
        }
    };

    const handleLogout = async () => {
        await disconnect();
        await logout();
        // Close offcanvas after logout
        const offcanvas = document.getElementById('drawerOffcanvas');
        if (offcanvas) {
            const bsOffcanvas = (window as any).bootstrap?.Offcanvas?.getInstance(offcanvas);
            bsOffcanvas?.hide();
        }
    };

    const handleViewChange = (view: ViewType) => {
        if (onViewChange) {
            onViewChange(view);
        }
        // Close offcanvas after view change
        closeOffcanvas();
    };

    const closeOffcanvas = () => {
        const offcanvas = document.getElementById('drawerOffcanvas');
        if (offcanvas) {
            const bsOffcanvas = (window as any).bootstrap?.Offcanvas?.getInstance(offcanvas);
            bsOffcanvas?.hide();
        }
    };

    const handleNavigate = (url: string) => {
        closeOffcanvas();
        // Small delay to ensure menu closes before navigation
        setTimeout(() => {
            window.location.href = url;
        }, 150);
    };

    return (
        <div className="offcanvas offcanvas-start" tabIndex={-1} id="drawerOffcanvas">
            <div className="offcanvas-header border-bottom">
                <h5 className="offcanvas-title fw-700">Menu</h5>
                <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
            </div>
            <div className="offcanvas-body p-0">
                <div className="p-4">
                    {/* Navigation Menu */}
                    <div className="nav-menu">
                        <div className="nav-item d-flex align-items-center py-3 cursor-pointer" onClick={() => handleViewChange('home')}>
                            <i className="feather-home font-lg me-3 text-grey-700"></i>
                            <span className="font-sm fw-600 text-grey-900">Home</span>
                        </div>
                        {user?.role === 'creator' && (
                            <div className="nav-item d-flex align-items-center py-3 cursor-pointer" onClick={() => handleViewChange('notifications')}>
                                <i className="feather-bell font-lg me-3 text-grey-700"></i>
                                <span className="font-sm fw-600 text-grey-900">Notifications</span>
                            </div>
                        )}
                        <div className="nav-item d-flex align-items-center py-3 cursor-pointer" onClick={() => handleViewChange('messages')}>
                            <i className="feather-message-circle font-lg me-3 text-grey-700"></i>
                            <span className="font-sm fw-600 text-grey-900">Messages</span>
                        </div>
                        <div className="nav-item d-flex align-items-center py-3 cursor-pointer" onClick={() => handleViewChange('collections')}>
                            <i className="feather-bookmark font-lg me-3 text-grey-700"></i>
                            <span className="font-sm fw-600 text-grey-900">Collections</span>
                        </div>
                        <div className="nav-item d-flex align-items-center py-3 cursor-pointer" onClick={() => handleViewChange('subscriptions')}>
                            <i className="feather-credit-card font-lg me-3 text-grey-700"></i>
                            <span className="font-sm fw-600 text-grey-900">Subscriptions</span>
                        </div>
                        <div className="nav-item d-flex align-items-center py-3 cursor-pointer" onClick={() => handleNavigate('/profile-search')}>
                            <i className="feather-search font-lg me-3 text-grey-700"></i>
                            <span className="font-sm fw-600 text-grey-900">Find Profiles</span>
                        </div>
                        <div className="nav-item d-flex align-items-center py-3 cursor-pointer" onClick={() => handleNavigate('/my-subscriptions')}>
                            <i className="feather-users font-lg me-3 text-grey-700"></i>
                            <span className="font-sm fw-600 text-grey-900">My Subscriptions</span>
                        </div>
                        <div className="nav-item d-flex align-items-center py-3 cursor-pointer" onClick={() => handleViewChange('creator-application')}>
                            <i className="feather-plus-circle font-lg me-3 text-grey-700"></i>
                            <span className="font-sm fw-600 text-grey-900">Creator Application</span>
                        </div>
                        <div className="nav-item d-flex align-items-center py-3 cursor-pointer" onClick={() => handleNavigate('/creator-settings')}>
                            <i className="feather-settings font-lg me-3 text-grey-700"></i>
                            <span className="font-sm fw-600 text-grey-900">Creator Settings</span>
                        </div>
                        <div className="nav-item d-flex align-items-center py-3 cursor-pointer" onClick={() => handleViewChange('profile')}>
                            <i className="feather-user font-lg me-3 text-grey-700"></i>
                            <span className="font-sm fw-600 text-grey-900">My profile</span>
                        </div>
                        <div className="nav-item d-flex align-items-center py-3 cursor-pointer" onClick={() => handleViewChange('analytics')}>
                            <i className="feather-bar-chart font-lg me-3 text-grey-700"></i>
                            <span className="font-sm fw-600 text-grey-900">Analytics</span>
                        </div>
                        {/* Admin Dashboard - only show for admin user */}
                        {user?.role === 'admin' && (
                            <>
                                <div className="nav-item d-flex align-items-center py-3 cursor-pointer" onClick={() => handleViewChange('admin-dashboard')}>
                                    <i className="feather-shield font-lg me-3 text-warning"></i>
                                    <span className="font-sm fw-600 text-warning">Admin Dashboard</span>
                                </div>
                                <div className="nav-item d-flex align-items-center py-3 cursor-pointer" onClick={() => handleViewChange('kyc-admin')}>
                                    <i className="feather-check-circle font-lg me-3 text-success"></i>
                                    <span className="font-sm fw-600 text-success">KYC Management</span>
                                </div>
                            </>
                        )}
                        {/* Logout Button */}
                        <div className="nav-item d-flex align-items-center py-3 cursor-pointer" onClick={handleLogout}>
                            <i className="feather-log-out font-lg me-3 text-danger"></i>
                            <span className="font-sm fw-600 text-danger">Logout</span>
                        </div>
                    </div>

                    {/* New Post Button */}
                    <div className="mt-4">
                        <button
                            className="btn btn-primary w-100 rounded-pill py-3 font-sm fw-700"
                            onClick={handleCreatePost}
                        >
                            NEW POST
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DrawerMenu;
